import { useEffect, useRef, useState } from 'react';

const projects = [
  {
    eyebrow: 'Especificaciones · 3 Recámaras',
    title: 'Casas de Campo Premium',
    desc: 'Diseño minimalista de 1 y 2 niveles en terrenos de 410 m². Balcones con vista al paisajismo natural de Cajamarca, estacionamiento privado y acabados de primer nivel que fusionan modernidad con el entorno natural.',
    img: '/assets/especificaciones/casas-campo.jpg',
    tag: '10 unidades en preventa',
  },
  {
    eyebrow: 'Ubicación · Baños del Inca',
    title: 'Entorno Natural Privilegiado',
    desc: 'A tan solo 14 minutos del centro de Baños del Inca, el proyecto está rodeado de vegetación exuberante y vistas panorámicas del paisaje cajamarquino. Una ubicación de alta plusvalía con crecimiento sostenido en zona turística.',
    img: '/assets/especificaciones/entorno-natural.jpg',
    tag: '14 min al centro',
  },
  {
    eyebrow: 'Comodidades · Resort Privado',
    title: 'Vida de Resort Todo el Año',
    desc: 'Zona social exclusiva, parrillas, spa, canchas deportivas, restaurant, minimarket y seguridad 24/7 — todo dentro de un condominio cerrado. Comodidades diseñadas para que nunca necesites salir.',
    img: '/assets/especificaciones/vida-resort.jpg',
    tag: '+6 comodidades exclusivas',
  },
  {
    eyebrow: 'Inversión · Alta Plusvalía',
    title: 'Genera Ingresos desde el Día 1',
    desc: 'Zona de crecimiento turístico acelerado en el norte del Perú. Administración profesional para alquiler vacacional en Airbnb incluida. Retorno estimado entre 8% y 12% anual con gestión sin complicaciones.',
    img: '/assets/especificaciones/ingresos.jpg',
    tag: '8–12% retorno anual est.',
  },
  {
    eyebrow: 'Arquitectura · Concepto',
    title: 'Diseño que Respeta la Naturaleza',
    desc: 'Arquitectura moderna integrada armoniosamente con el entorno cajamarquino. Materiales de primera calidad, paleta neutra y líneas limpias que dialogan con el paisaje natural sin interrumpirlo.',
    img: '/assets/especificaciones/diseno.jpg',
    tag: 'Diseño premium certificado',
  },
];

export default function Projects() {
  const [active, setActive] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const { top, height } = el.getBoundingClientRect();
      const scrollable = height - window.innerHeight;
      const progress = Math.max(0, Math.min(1, -top / scrollable));
      const idx = Math.min(projects.length - 1, Math.floor(progress * projects.length));
      setActive(idx);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section id="especificaciones">
      {/* Tall scroll wrapper */}
      <div ref={wrapperRef} style={{ height: `${projects.length * 100}vh` }}>
        {/* Sticky viewport */}
        <div className="sticky top-0 h-screen overflow-hidden grid grid-rows-[3fr_2fr] md:grid-rows-1 md:grid-cols-2">

          {/* ── Left: text ── */}
          <div className="relative flex flex-col justify-between px-6 md:px-16 py-8 md:py-14 overflow-hidden bg-white">
            {/* Header */}
            <div>
              <p className="text-xs tracking-[.28em] text-gray-400 uppercase font-medium mb-1">El Proyecto</p>
              <h2 className="text-2xl font-bold text-black leading-tight">
                Una mirada al <span className="font-light italic">Alto de los Bosques</span>
              </h2>
            </div>

            {/* Cards text — stacked, fade between */}
            <div className="flex-1 flex items-center">
              <div className="relative w-full" style={{ minHeight: 260 }}>
                {projects.map((p, i) => (
                  <div
                    key={i}
                    className="absolute inset-0 flex flex-col justify-center"
                    style={{
                      opacity: i === active ? 1 : 0,
                      transform: i === active ? 'translateY(0)' : i < active ? 'translateY(-18px)' : 'translateY(18px)',
                      transition: 'opacity 0.55s ease, transform 0.55s ease',
                      pointerEvents: i === active ? 'auto' : 'none',
                    }}
                  >
                    <p className="text-[10px] tracking-[.24em] text-gray-400 uppercase font-semibold mb-3">{p.eyebrow}</p>
                    <h3 className="text-2xl md:text-4xl font-bold text-black leading-tight mb-3 md:mb-4">{p.title}</h3>
                    <p className="text-gray-500 leading-relaxed font-light text-sm max-w-md">{p.desc}</p>
                    <span className="mt-5 self-start text-[10px] tracking-[.2em] uppercase font-semibold text-gray-500 bg-gray-100 rounded-full px-3 py-1.5">
                      {p.tag}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar + counter */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-100 relative overflow-hidden rounded-full">
                <div
                  className="absolute left-0 top-0 h-full bg-black rounded-full"
                  style={{
                    width: `${((active + 1) / projects.length) * 100}%`,
                    transition: 'width 0.55s ease',
                  }}
                />
              </div>
              <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                {String(active + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* ── Right: images ── */}
          <div className="relative overflow-hidden">
            {projects.map((p, i) => (
              <div
                key={i}
                className="absolute inset-0"
                style={{
                  opacity: i === active ? 1 : 0,
                  transform: i === active ? 'scale(1)' : 'scale(1.04)',
                  transition: 'opacity 0.65s ease, transform 0.65s ease',
                }}
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}

            {/* Dot nav */}
            <div className="absolute right-5 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
              {projects.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full bg-white transition-all duration-300"
                  style={{
                    width: i === active ? 8 : 5,
                    height: i === active ? 8 : 5,
                    opacity: i === active ? 1 : 0.45,
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
      {/* Separator */}
      <div className="px-8 md:px-16 py-10">
        <div className="border-t border-gray-200" />
      </div>
    </section>
  );
}
